/**
 * Tier presentation helpers (J6, Wave 3).
 *
 * `AUTONOMY_TIER_LABELS` is the single FE copy source for tier naming (G8) —
 * it MUST stay byte-equal to the backend `AUTONOMY_TIER_LABELS` in
 * `builder-service/src/services/AutonomySettingsService.ts`: the label shown
 * is the label the write gate enforces.
 *
 * Lock/selectability helpers only READ server-computed `tierOptions` fields —
 * the frontend never derives lock verdicts. A tier missing from the server
 * options resolves locked/not-selectable (fail closed).
 */

import { AUTONOMY_TIERS } from '../types/autonomy.types';
import type { AutonomyLeverState, AutonomyTier, AutonomyTierOption } from '../types/autonomy.types';

/** Ordered L1 → L5 (render order of every lever row). */
export const AUTONOMY_TIER_SEQUENCE: readonly AutonomyTier[] = AUTONOMY_TIERS;

/** G8 single FE copy source — mirrors backend AUTONOMY_TIER_LABELS verbatim. */
export const AUTONOMY_TIER_LABELS: Record<AutonomyTier, string> = {
  L1: 'Observe',
  L2: 'Suggest',
  L3: 'Draft',
  L4: 'Apply with approval',
  L5: 'Autopilot',
};

export const AUTONOMY_TIER_ORDER: Record<AutonomyTier, number> = {
  L1: 1,
  L2: 2,
  L3: 3,
  L4: 4,
  L5: 5,
};

/** "L2 Suggest" — toggle-option label. */
export function tierToggleLabel(tier: AutonomyTier): string {
  return `${tier} ${AUTONOMY_TIER_LABELS[tier]}`;
}

/** "L2 (Suggest)" — inline-copy form used in refusal lines and errors. */
export function formatTier(tier: AutonomyTier): string {
  return `${tier} (${AUTONOMY_TIER_LABELS[tier]})`;
}

/** Negative when a < b, 0 when equal, positive when a > b. */
export function compareTiers(a: AutonomyTier, b: AutonomyTier): number {
  return AUTONOMY_TIER_ORDER[a] - AUTONOMY_TIER_ORDER[b];
}

/**
 * Lever-aware wGate refusal line (AC-006 copy pattern), e.g.
 * "copy lever is at L2 (Suggest) — L3 (Draft) required."
 */
export function formatLeverRefusal(
  lever: string,
  currentTier: AutonomyTier,
  requiredTier: AutonomyTier,
): string {
  return `${lever} lever is at ${formatTier(currentTier)} — ${formatTier(requiredTier)} required.`;
}

/** Server option for a tier, if the server sent one. */
export function getTierOption(
  lever: AutonomyLeverState,
  tier: AutonomyTier,
): AutonomyTierOption | undefined {
  return lever.tierOptions.find((option) => option.tier === tier);
}

/** Fail closed: a tier the server did not describe is locked. */
export function isTierLocked(lever: AutonomyLeverState, tier: AutonomyTier): boolean {
  const option = getTierOption(lever, tier);
  return option ? option.locked : true;
}

/** Fail closed: a tier the server did not describe is not selectable. */
export function isTierSelectable(lever: AutonomyLeverState, tier: AutonomyTier): boolean {
  const option = getTierOption(lever, tier);
  return option ? option.selectable && !option.locked : false;
}

/** Server-provided lock reason for a tier, if any. */
export function getLockReason(lever: AutonomyLeverState, tier: AutonomyTier): string | undefined {
  return getTierOption(lever, tier)?.lockReason;
}

/**
 * Distinct lock reasons of a lever row in tier order — the row renders each
 * reason once (L4/L5 share the approval-machinery reason; pricing adds the
 * permanent-ceiling reason).
 */
export function distinctLockReasons(lever: AutonomyLeverState): string[] {
  const reasons: string[] = [];
  for (const tier of AUTONOMY_TIER_SEQUENCE) {
    const reason = getLockReason(lever, tier);
    if (reason && !reasons.includes(reason)) {
      reasons.push(reason);
    }
  }
  return reasons;
}

/** One rendered lock-reason line of a lever row (see {@link lockReasonGroups}). */
export interface LockReasonGroup {
  /** Server-provided lock reason, verbatim. */
  reason: string;
  /** Tiers (in L1→L5 order) that carry this reason. */
  tiers: AutonomyTier[];
  /**
   * True when every tier of the group sits ABOVE the server ceiling — the
   * permanent-cap styling (mock: pricing L5 renders the ceiling reason in
   * warning color with no tier prefix). Purely positional: the verdict
   * (ceiling + lockReason) is still the server's.
   */
  aboveCeiling: boolean;
}

/**
 * Groups the server lock reasons of a lever row for rendering, per the
 * confirmed mock: each distinct reason renders once with a tier prefix
 * ("L4–L5: …" / "L4: …"), except reasons on tiers above the server ceiling
 * (pricing L5) which render first, unprefixed, in ceiling styling.
 */
export function lockReasonGroups(lever: AutonomyLeverState): LockReasonGroup[] {
  const groups: LockReasonGroup[] = [];
  for (const tier of AUTONOMY_TIER_SEQUENCE) {
    const reason = getLockReason(lever, tier);
    if (!reason) continue;
    const aboveCeiling = compareTiers(tier, lever.ceiling) > 0;
    const existing = groups.find(
      (group) => group.reason === reason && group.aboveCeiling === aboveCeiling,
    );
    if (existing) {
      existing.tiers.push(tier);
    } else {
      groups.push({ reason, tiers: [tier], aboveCeiling });
    }
  }
  // Ceiling reasons first — the mock renders the pricing permanent-cap line
  // above the approval-machinery lock line.
  return [
    ...groups.filter((group) => group.aboveCeiling),
    ...groups.filter((group) => !group.aboveCeiling),
  ];
}

/** "L4–L5" for a multi-tier group, "L4" for a single tier, "" when empty. */
export function formatTierRange(tiers: AutonomyTier[]): string {
  if (tiers.length === 0) return '';
  if (tiers.length === 1) return tiers[0];
  return `${tiers[0]}–${tiers[tiers.length - 1]}`;
}
